import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession
} from 'amazon-cognito-identity-js'

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID!,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID!
}

const userPool = new CognitoUserPool(poolData)

export interface CognitoAuthUser {
  userId: string
  email: string
  tier: string
  credits: number
  stripeCustomerId?: string
  accessToken: string
  idToken: string
  refreshToken: string
}

// Error thrown when user needs to set a new password
export class NewPasswordRequiredError extends Error {
  cognitoUser: CognitoUser
  userAttributes: Record<string, string>

  constructor(cognitoUser: CognitoUser, userAttributes: Record<string, string>) {
    super('NEW_PASSWORD_REQUIRED')
    this.name = 'NewPasswordRequiredError'
    this.cognitoUser = cognitoUser
    this.userAttributes = userAttributes
  }
}

// Sign up new user
export async function signUp(email: string, password: string, referralCode?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const attributeList = [
      new CognitoUserAttribute({
        Name: 'email',
        Value: email
      })
    ]

    // Store referral code in localStorage for use after first login
    if (referralCode) {
      localStorage.setItem('pendingReferralCode', referralCode)
    }

    userPool.signUp(email, password, attributeList, [], (err, result) => {
      if (err) {
        reject(err)
        return
      }
      resolve()
    })
  })
}

// Sign in user
export async function signIn(email: string, password: string): Promise<CognitoAuthUser> {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password
    })

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    })

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session: CognitoUserSession) => {
        const idToken = session.getIdToken()
        const payload = idToken.payload

        resolve({
          userId: payload.sub,
          email: payload.email || email,
          tier: payload['custom:tier'] || 'STARTER',
          credits: parseInt(payload['custom:credits'] || '0'),
          stripeCustomerId: payload['custom:stripeId'],
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: idToken.getJwtToken(),
          refreshToken: session.getRefreshToken().getToken()
        })
      },
      onFailure: (err) => {
        reject(err)
      },
      newPasswordRequired: () => {
        // For users created by admin with temporary password, they need to set a new password
        // Pass empty attributes - the app client only has write access to 'email'
        // and passing any custom attributes (tier, credits, etc.) causes Cognito to reject the call
        reject(new NewPasswordRequiredError(cognitoUser, {}))
      }
    })
  })
}

// Sign out
export function signOut(): void {
  const cognitoUser = userPool.getCurrentUser()
  if (cognitoUser) {
    cognitoUser.signOut()
  }
}

// Complete new password challenge for users with temporary passwords
export async function completeNewPassword(
  cognitoUser: CognitoUser,
  newPassword: string,
  userAttributes: Record<string, string>
): Promise<CognitoAuthUser> {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session: CognitoUserSession) => {
        const idToken = session.getIdToken()
        const payload = idToken.payload

        resolve({
          userId: payload.sub,
          email: payload.email || cognitoUser.getUsername(),
          tier: payload['custom:tier'] || 'STARTER',
          credits: parseInt(payload['custom:credits'] || '0'),
          stripeCustomerId: payload['custom:stripeCustomerId'],
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: idToken.getJwtToken(),
          refreshToken: session.getRefreshToken().getToken()
        })
      },
      onFailure: (err) => {
        reject(err)
      }
    })
  })
}

// Get current session
export async function getCurrentSession(): Promise<CognitoAuthUser | null> {
  // E2E test mode - return mock auth data
  if ((window as any).__E2E_TEST__ && (window as any).__MOCK_AUTH__) {
    const mockAuth = (window as any).__MOCK_AUTH__;
    return Promise.resolve({
      userId: mockAuth.userId,
      email: mockAuth.email,
      tier: mockAuth.tier,
      credits: mockAuth.credits,
      accessToken: mockAuth.accessToken,
      idToken: mockAuth.idToken,
      refreshToken: mockAuth.refreshToken
    });
  }

  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser()

    if (!cognitoUser) {
      resolve(null)
      return
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null)
        return
      }

      const idToken = session.getIdToken()
      const payload = idToken.payload

      resolve({
        userId: payload.sub,
        email: payload.email || '',
        tier: payload['custom:tier'] || 'STARTER',
        credits: parseInt(payload['custom:credits'] || '0'),
        stripeCustomerId: payload['custom:stripeCustomerId'],
        accessToken: session.getAccessToken().getJwtToken(),
        idToken: idToken.getJwtToken(),
        refreshToken: session.getRefreshToken().getToken()
      })
    })
  })
}

// Refresh session
export async function refreshSession(): Promise<CognitoAuthUser | null> {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser()

    if (!cognitoUser) {
      resolve(null)
      return
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(err)
        return
      }

      const refreshToken = session.getRefreshToken()

      cognitoUser.refreshSession(refreshToken, (err, session) => {
        if (err) {
          reject(err)
          return
        }

        const idToken = session.getIdToken()
        const payload = idToken.payload

        resolve({
          userId: payload.sub,
          email: payload.email || '',
          tier: payload['custom:tier'] || 'STARTER',
          credits: parseInt(payload['custom:credits'] || '0'),
          stripeCustomerId: payload['custom:stripeCustomerId'],
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: idToken.getJwtToken(),
          refreshToken: session.getRefreshToken().getToken()
        })
      })
    })
  })
}

// Update user attributes (credits, tier)
export async function updateUserAttributes(attributes: {
  tier?: string
  credits?: number
  stripeCustomerId?: string
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser()

    if (!cognitoUser) {
      reject(new Error('No current user'))
      return
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(err)
        return
      }

      const attributeList: CognitoUserAttribute[] = []

      if (attributes.tier) {
        attributeList.push(new CognitoUserAttribute({
          Name: 'custom:tier',
          Value: attributes.tier
        }))
      }

      if (attributes.credits !== undefined) {
        attributeList.push(new CognitoUserAttribute({
          Name: 'custom:credits',
          Value: attributes.credits.toString()
        }))
      }

      if (attributes.stripeCustomerId) {
        attributeList.push(new CognitoUserAttribute({
          Name: 'custom:stripeId',
          Value: attributes.stripeCustomerId
        }))
      }

      cognitoUser.updateAttributes(attributeList, (err) => {
        if (err) {
          reject(err)
          return
        }
        resolve()
      })
    })
  })
}

// Verify email with confirmation code
export async function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    })

    cognitoUser.confirmRegistration(code, true, (err) => {
      if (err) {
        reject(err)
        return
      }
      resolve()
    })
  })
}

// Resend verification code
export async function resendConfirmationCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    })

    cognitoUser.resendConfirmationCode((err) => {
      if (err) {
        reject(err)
        return
      }
      resolve()
    })
  })
}

// Forgot password - send verification code
export async function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    })

    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve()
      },
      onFailure: (err) => {
        reject(err)
      }
    })
  })
}

// Confirm new password with verification code
export async function confirmPassword(email: string, code: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    })

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve()
      },
      onFailure: (err) => {
        reject(err)
      }
    })
  })
}

// Change password for logged-in user
export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser()

    if (!cognitoUser) {
      reject(new Error('No current user'))
      return
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(err)
        return
      }

      cognitoUser.changePassword(oldPassword, newPassword, (err) => {
        if (err) {
          reject(err)
          return
        }
        resolve()
      })
    })
  })
}
